import React, { useState, useEffect, useRef } from 'react';
import { Button, Typography } from '@material-ui/core';
import styled from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import { setCurrentlyPlayingProviderRecordingId } from 'state/app.state';
import { RootState } from 'state/store';

interface Props {
  audioUrl: string | undefined;
  providerRecordingId: string | undefined;
}

const StyledButton = styled(Button)`
  vertical-align: inherit;
  text-transform: none;
  padding: ${(props) => props.theme.spacing(0, 0.25)};
  color: ${(props) => props.theme.palette.secondary.main};
`;

const CallRecording = ({ audioUrl, providerRecordingId }: Props) => {
  const dispatch = useDispatch();

  const currentlyPlayingProviderRecordingId = useSelector(
    (state: RootState) => state.app.currentlyPlayingProviderRecordingId
  );
  const [isPlaying, setIsPlaying] = useState(false);
  const audioElement = useRef(new Audio()).current;
  const [inViewport, setInViewport] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const toggleAudio = () => {
    if (isPlaying) {
      dispatch(setCurrentlyPlayingProviderRecordingId(null));
    } else {
      dispatch(setCurrentlyPlayingProviderRecordingId(providerRecordingId));
    }
    setIsPlaying(!isPlaying);
  };

  // Pausing other instances of audio when a new one is played
  useEffect(() => {
    if (
      providerRecordingId !== currentlyPlayingProviderRecordingId &&
      currentlyPlayingProviderRecordingId !== null &&
      inViewport
    ) {
      setIsPlaying(false);
      audioElement.pause();
    }
  }, [currentlyPlayingProviderRecordingId, inViewport, providerRecordingId, audioElement]);

  // Observer for lazy Loading
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        setInViewport(entry.isIntersecting);
      },
      { threshold: 1 } // 100% of element is visible
    );

    if (audioRef?.current) {
      observer.observe(audioRef?.current);
    }

    // cleanup observer
    return () => {
      observer.disconnect();
    };
  }, [audioUrl]);

  // Playback logic
  useEffect(() => {
    if (inViewport && audioUrl) {
      const transformAndSetAudioElement = async () => {
        audioElement.src = audioUrl;

        if (isPlaying) {
          const playPromise = audioElement.play();

          if (playPromise) {
            playPromise.catch((error) => {
              console.error('Error while trying to play audio:', error);
            });
          } else {
            audioElement.pause();
            audioElement.currentTime = 0;
          }
        }
      };

      transformAndSetAudioElement();
    }

    // Cleanup: Pause the audio when the component unmounts
    return () => {
      audioElement.pause();
      audioElement.currentTime = 0;
    };
  }, [audioUrl, inViewport, isPlaying, audioElement]);

  return (
    <StyledButton ref={audioRef as React.RefObject<HTMLButtonElement>} onClick={toggleAudio}>
      <Typography variant="caption">{isPlaying ? 'Stop recording' : 'Play recording'}</Typography>
    </StyledButton>
  );
};

export default CallRecording;
